import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
  Container,
  Typography,
  Menu,
  MenuItem,
  Collapse,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { colors } from '../theme';

interface SubMenuItem {
  label: string;
  path: string;
}

interface NavItem {
  label: string;
  path: string;
  subItems?: SubMenuItem[];
}

const navItems: NavItem[] = [
  {
    label: 'Products',
    path: '/products',
    subItems: [
      { label: 'VFD Panels', path: '/products/vfd-panels' },
      { label: 'Chemical Storage Tanks', path: '/products/chemical-storage-tanks' },
      { label: 'Combination Starter Panels', path: '/products/combination-starter-panels' },
      { label: 'Electrical Control Product', path: '/products/electrical-control-product' },
    ],
  },
  { label: 'Process Control', path: '/process-control' },
  { label: 'Services', path: '/services' },
  { label: 'Suppliers', path: '/suppliers' },
  { label: 'About Us', path: '/about' },
  { label: 'Contact Us', path: '/contact' },
];

const Navbar: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
    setMobileSubmenuOpen(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMobileSubmenuToggle = (label: string) => {
    setMobileSubmenuOpen(mobileSubmenuOpen === label ? null : label);
  };

  const isProductsPath = location.pathname.startsWith('/products');

  const drawer = (
    <Box
      sx={{
        height: '100%',
        background: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2.5, borderBottom: `1px solid ${colors.border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src="/Logo-RPM.png"
            alt="RPM Control"
            sx={{ height: 36, width: 'auto' }}
          />
          <Box sx={{ width: 1, height: 28, bgcolor: colors.border }} />
          <Box
            component="img"
            src="/Logo-Process.png"
            alt="Process Control"
            sx={{ height: 36, width: 'auto' }}
          />
        </Box>
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            color: colors.secondary,
            '&:hover': {
              backgroundColor: 'rgba(245, 166, 35, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <List sx={{ flex: 1, pt: 1 }}>
        {navItems.map((item) => (
          <React.Fragment key={item.label}>
            {item.subItems ? (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleMobileSubmenuToggle(item.label)}
                    sx={{
                      py: 1.5,
                      px: 3,
                      borderLeft: isProductsPath ? `3px solid ${colors.primary}` : '3px solid transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(245, 166, 35, 0.08)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        sx: {
                          fontWeight: isProductsPath ? 600 : 500,
                          fontSize: '1rem',
                          color: isProductsPath ? colors.primary : colors.secondary,
                        },
                      }}
                    />
                    {mobileSubmenuOpen === item.label ? <ExpandLess sx={{ color: colors.primary }} /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={mobileSubmenuOpen === item.label} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                    <ListItem disablePadding>
                      <ListItemButton
                        component={Link}
                        to={item.path}
                        onClick={handleDrawerToggle}
                        sx={{
                          py: 1.25,
                          pl: 5,
                          '&:hover': {
                            backgroundColor: 'rgba(245, 166, 35, 0.08)',
                          },
                        }}
                      >
                        <ListItemText
                          primary="All Products"
                          primaryTypographyProps={{
                            sx: {
                              fontSize: '0.9rem',
                              fontWeight: 500,
                              color: location.pathname === item.path ? colors.primary : colors.textSecondary,
                            },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                    {item.subItems.map((subItem) => (
                      <ListItem key={subItem.label} disablePadding>
                        <ListItemButton
                          component={Link}
                          to={subItem.path}
                          onClick={handleDrawerToggle}
                          sx={{
                            py: 1.25,
                            pl: 5,
                            '&:hover': {
                              backgroundColor: 'rgba(245, 166, 35, 0.08)',
                            },
                          }}
                        >
                          <ListItemText
                            primary={subItem.label}
                            primaryTypographyProps={{
                              sx: {
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                color: location.pathname === subItem.path ? colors.primary : colors.textSecondary,
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  onClick={handleDrawerToggle}
                  sx={{
                    py: 1.5,
                    px: 3,
                    borderLeft: location.pathname === item.path ? `3px solid ${colors.primary}` : '3px solid transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(245, 166, 35, 0.08)',
                    },
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: location.pathname === item.path ? 600 : 500,
                        fontSize: '1rem',
                        color: location.pathname === item.path ? colors.primary : colors.secondary,
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </React.Fragment>
        ))}
      </List>
      {/* Mobile Contact Info */}
      <Box sx={{ p: 3, borderTop: `1px solid ${colors.border}`, bgcolor: colors.background }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              backgroundColor: 'rgba(245, 166, 35, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PhoneIcon sx={{ color: colors.primary, fontSize: 18 }} />
          </Box>
          <Typography variant="body2" sx={{ color: colors.secondary, fontWeight: 500 }}>
            315-479-7262
          </Typography>
        </Box>
        <Button
          component={Link}
          to="/contact"
          fullWidth
          variant="contained"
          onClick={handleDrawerToggle}
          sx={{ py: 1.5 }}
        >
          Request a Quote
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Top Info Bar */}
      <Box
        sx={{
          bgcolor: colors.secondary,
          py: 1,
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon sx={{ color: colors.primary, fontSize: 16 }} />
              <Typography
                component="a"
                href="tel:3154797262"
                sx={{
                  color: '#FFFFFF',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: colors.primary },
                }}
              >
                315-479-7262
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon sx={{ color: colors.primary, fontSize: 16 }} />
              <Typography
                component="a"
                href="mailto:jsmith@processcontrolllc.com"
                sx={{
                  color: '#FFFFFF',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: colors.primary },
                }}
              >
                jsmith@processcontrolllc.com
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <AppBar
        position="sticky"
        sx={{
          background: '#FFFFFF',
          boxShadow: scrolled ? '0 4px 20px rgba(0, 0, 0, 0.08)' : 'none',
          borderBottom: `1px solid ${colors.border}`,
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1, minHeight: { xs: 70, md: 80 } }}>
            {/* Dual Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 } }}>
              <Link to="https://www.rpmcontrolcorp.com" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Box
                  component="img"
                  src="/Logo-RPM.png"
                  alt="RPM Control"
                  sx={{
                    height: { xs: 38, md: 50 },
                    width: 'auto',
                    transition: 'all 0.3s ease',
                    pr: { xs: 1.5, md: 2 },
                    borderRight: `1px solid ${colors.border}`,
                  }}
                />
              </Link>
              <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Box
                  component="img"
                  src="/Logo-Process.png"
                  alt="Process Control"
                  sx={{
                    height: { xs: 38, md: 50 },
                    width: 'auto',
                    transition: 'all 0.3s ease',
                    pl: { xs: 1.5, md: 2 },
                  }}
                />
              </Link>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {navItems.map((item) => (
                  item.subItems ? (
                    <Box key={item.label}>
                      <Button
                        onClick={handleMenuOpen}
                        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                        sx={{
                          color: isProductsPath ? colors.primary : colors.secondary,
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 4,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: isProductsPath ? '70%' : '0%',
                            height: 2,
                            backgroundColor: colors.primary,
                            borderRadius: 1,
                            transition: 'width 0.3s ease',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(245, 166, 35, 0.08)',
                            color: colors.primary,
                            '&::after': {
                              width: '70%',
                            },
                          },
                        }}
                      >
                        {item.label}
                      </Button>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        MenuListProps={{
                          onMouseLeave: handleMenuClose,
                        }}
                        sx={{
                          '& .MuiPaper-root': {
                            mt: 1.5,
                            borderRadius: 3,
                            borderTop: `3px solid ${colors.primary}`,
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
                            minWidth: 220,
                          },
                        }}
                      >
                        <MenuItem
                          component={Link}
                          to={item.path}
                          onClick={handleMenuClose}
                          sx={{
                            py: 1.5,
                            px: 3,
                            fontWeight: 500,
                            '&:hover': {
                              backgroundColor: 'rgba(245, 166, 35, 0.08)',
                              color: colors.primary,
                            },
                          }}
                        >
                          All Products
                        </MenuItem>
                        {item.subItems.map((subItem) => (
                          <MenuItem
                            key={subItem.label}
                            component={Link}
                            to={subItem.path}
                            onClick={handleMenuClose}
                            sx={{
                              py: 1.5,
                              px: 3,
                              fontWeight: 500,
                              color: location.pathname === subItem.path ? colors.primary : colors.secondary,
                              '&:hover': {
                                backgroundColor: 'rgba(245, 166, 35, 0.08)',
                                color: colors.primary,
                              },
                            }}
                          >
                            {subItem.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                  ) : (
                    <Button
                      key={item.label}
                      component={Link}
                      to={item.path}
                      sx={{
                        color: location.pathname === item.path ? colors.primary : colors.secondary,
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 4,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: location.pathname === item.path ? '70%' : '0%',
                          height: 2,
                          backgroundColor: colors.primary,
                          borderRadius: 1,
                          transition: 'width 0.3s ease',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(245, 166, 35, 0.08)',
                          color: colors.primary,
                          '&::after': {
                            width: '70%',
                          },
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  )
                ))}

                {/* CTA Button */}
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  sx={{
                    ml: 2,
                    px: 3,
                  }}
                >
                  Request a Quote
                </Button>
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  color: colors.secondary,
                  '&:hover': {
                    backgroundColor: 'rgba(245, 166, 35, 0.1)',
                  },
                }}
              >
                <MenuIcon sx={{ fontSize: 28 }} />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: 340,
            borderRadius: '16px 0 0 16px',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;
